import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PRIORITIES, DEFAULT_STATUSES } from '../lib/constants';
import {
  useLabelsQuery,
  useCustomStatusesQuery,
  useTaskTypesQuery,
  useCustomFieldsQuery,
  useTeamsQuery,
} from './queries';
import { useTaskFilters } from './useTaskFilters';
import { EMPTY_ARRAY } from '../lib/empty';

// Backward-compat aliases matching TasksTable's status/priority option shape.
const priorityConfig = Object.fromEntries(
  Object.entries(PRIORITIES).map(([k, v]) => [k, { ...v, color: v.bgColor, label: v.altLabel }]),
);
const statusConfig = Object.fromEntries(
  Object.entries(DEFAULT_STATUSES).map(([k, v]) => [k, { ...v, color: v.bgColor }]),
);

/**
 * Page-level convenience wrapper around useTaskFilters that gathers every data
 * dependency the filter engine needs (labels, custom statuses, task types,
 * custom fields, teams, current user) so a page can add shared filtering with a
 * single call. Pass the page's `tasks`, a persistence `viewId`, and `projects`.
 */
export function usePageTaskFilters({ tasks = [], viewId = null, projects = [], teamId = null, projectId = null }) {
  const { user } = useAuth();
  const labels = useLabelsQuery().data || [];
  const customStatuses = useCustomStatusesQuery(teamId).data || [];
  const taskTypes = useTaskTypesQuery().data || [];
  const customFieldDefs = useCustomFieldsQuery(projectId).data || [];
  const teams = useTeamsQuery().data || EMPTY_ARRAY;

  // usersMap / teamsMap — same construction as TasksTable: seed the current user
  // (so tasks assigned to me resolve on personal views) then layer team members.
  const { usersMap, teamsMap } = useMemo(() => {
    const uMap = {};
    const tMap = {};
    if (user?.id) {
      uMap[user.id] = { user_id: user.id, full_name: user.full_name, avatar_url: user.avatar_url, email: user.email };
    }
    (teams || []).forEach(team => {
      tMap[team.id] = team;
      (team.members || []).forEach(m => { uMap[m.user_id] = m; });
    });
    return { usersMap: uMap, teamsMap: tMap };
  }, [teams, user]);

  return useTaskFilters({
    tasks,
    viewId,
    currentUserId: user?.id || null,
    customStatuses,
    statusConfig,
    priorityConfig,
    projects,
    labels,
    taskTypes,
    usersMap,
    teamsMap,
    customFieldDefs,
  });
}
